import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Page, PageHeader, StatCard, fmtEur } from "@/components/ui-kit";
import { BarChart3, TrendingUp, Trophy, Package as PackageIcon, Store, Filter } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from "recharts";
import { getNegozioFilter, NEGOZIO_FILTER_EVENT } from "@/components/AppLayout";

const COLORS = ["#1FAE52", "#0A0A0A", "#3B82F6", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6"];

function firstOfMonth(iso) {
  if (!iso) return "";
  return iso.slice(0, 7);
}

function shiftMonths(months) {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return d.toISOString().slice(0, 10);
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [negozioFilter, setLocalNegozio] = useState(getNegozioFilter());
  const [negozi, setNegozi] = useState([]);
  const [periodo, setPeriodo] = useState("12m"); // "3m" | "6m" | "12m" | "ytd" | "all"

  useEffect(() => {
    api.get("/negozi").then((r) => setNegozi(r.data || [])).catch(() => {});
    const handler = (e) => setLocalNegozio(e.detail || "");
    window.addEventListener(NEGOZIO_FILTER_EVENT, handler);
    return () => window.removeEventListener(NEGOZIO_FILTER_EVENT, handler);
  }, []);

  const daISO = useMemo(() => {
    if (periodo === "3m") return shiftMonths(3);
    if (periodo === "6m") return shiftMonths(6);
    if (periodo === "12m") return shiftMonths(12);
    if (periodo === "ytd") return `${new Date().getFullYear()}-01-01`;
    return "";
  }, [periodo]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (negozioFilter) params.negozio_id = negozioFilter;
      if (daISO) params.da = daISO;
      const r = await api.get("/stats/analytics", { params });
      setData(r.data || {});
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [negozioFilter, daISO]);

  const perMese = data?.per_mese || [];
  const perPacchetto = data?.per_pacchetto || [];
  const topVend = data?.top_venditori || [];
  const statiPrev = data?.preventivi_per_stato || {};
  const statiCom = data?.commesse_per_stato || {};

  const conversionRate = useMemo(() => {
    const tot = (statiPrev.bozza || 0) + (statiPrev.inviato || 0) + (statiPrev.accettato || 0) + (statiPrev.rifiutato || 0);
    if (!tot) return 0;
    return Math.round(((statiPrev.accettato || 0) / tot) * 100);
  }, [statiPrev]);

  const pieStatiPrev = useMemo(() => Object.entries(statiPrev).filter(([, v]) => v > 0).map(([k, v]) => ({ name: k, value: v })), [statiPrev]);
  const pieStatiCom = useMemo(() => Object.entries(statiCom).filter(([, v]) => v > 0).map(([k, v]) => ({ name: k, value: v })), [statiCom]);

  return (
    <div>
      <PageHeader
        title="Analytics"
        subtitle="Report vendite, commesse e performance venditori in tempo reale"
      />
      <Page>
        {/* Filtri */}
        <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="analytics-filters">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
            <Filter size={14} /> Filtri
          </div>
          <select
            className="text-sm border border-zinc-300 rounded-lg px-3 py-2"
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            data-testid="analytics-periodo"
          >
            <option value="3m">Ultimi 3 mesi</option>
            <option value="6m">Ultimi 6 mesi</option>
            <option value="12m">Ultimi 12 mesi</option>
            <option value="ytd">Anno corrente</option>
            <option value="all">Tutti i dati</option>
          </select>
          <select
            className="text-sm border border-zinc-300 rounded-lg px-3 py-2"
            value={negozioFilter}
            onChange={(e) => { setLocalNegozio(e.target.value); }}
            data-testid="analytics-negozio"
          >
            <option value="">Tutti i negozi</option>
            {negozi.map((n) => (
              <option key={n.id} value={n.id}>{n.name || n.nome || n.id}</option>
            ))}
          </select>
          {loading && <span className="text-xs text-zinc-500 animate-pulse">Caricamento…</span>}
        </div>

        {/* KPI */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="analytics-kpis">
          <StatCard label="Preventivi totali" value={data?.preventivi_totali ?? 0} />
          <StatCard label="Accettati" value={statiPrev.accettato ?? 0} color="text-emerald-600" />
          <StatCard label="Tasso conversione" value={`${conversionRate}%`} color={conversionRate >= 30 ? "text-emerald-600" : "text-amber-600"} />
          <StatCard label="Totale venduto" value={fmtEur(data?.totale_venduto ?? 0)} />
          <StatCard label="Fatturato commesse" value={fmtEur(data?.fatturato_totale ?? 0)} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
          {/* Andamento mensile */}
          <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid="chart-per-mese">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp size={16} className="text-[#1FAE52]" />
              <h3 className="text-sm font-bold uppercase tracking-wide text-zinc-700">Venduto per mese</h3>
            </div>
            {perMese.length === 0 ? (
              <div className="text-center text-zinc-500 py-12 text-sm">Nessun dato nel periodo selezionato</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={perMese}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="mese" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => fmtEur(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="venduto" stroke="#1FAE52" strokeWidth={2} name="Venduto (€)" />
                  <Line type="monotone" dataKey="count" stroke="#0A0A0A" strokeWidth={2} name="N. accettati" yAxisId="right" />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Top pacchetti */}
          <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid="chart-top-pacchetti">
            <div className="flex items-center gap-2 mb-4">
              <PackageIcon size={16} className="text-[#1FAE52]" />
              <h3 className="text-sm font-bold uppercase tracking-wide text-zinc-700">Top pacchetti venduti</h3>
            </div>
            {perPacchetto.length === 0 ? (
              <div className="text-center text-zinc-500 py-12 text-sm">Nessun dato</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={perPacchetto.slice(0, 8)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <YAxis dataKey="key" type="category" tick={{ fontSize: 11 }} width={100} />
                  <Tooltip formatter={(v) => fmtEur(v)} />
                  <Bar dataKey="venduto" fill="#1FAE52" name="Venduto" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
          {/* Stati preventivi pie */}
          <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid="chart-stati-prev">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 size={16} className="text-[#1FAE52]" />
              <h3 className="text-sm font-bold uppercase tracking-wide text-zinc-700">Preventivi per stato</h3>
            </div>
            {pieStatiPrev.length === 0 ? (
              <div className="text-center text-zinc-500 py-12 text-sm">Nessun dato</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={pieStatiPrev} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} label={(e) => `${e.name}: ${e.value}`}>
                    {pieStatiPrev.map((entry, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Stati commesse pie */}
          <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid="chart-stati-com">
            <div className="flex items-center gap-2 mb-4">
              <Store size={16} className="text-[#1FAE52]" />
              <h3 className="text-sm font-bold uppercase tracking-wide text-zinc-700">Commesse per stato</h3>
            </div>
            {pieStatiCom.length === 0 ? (
              <div className="text-center text-zinc-500 py-12 text-sm">Nessun dato</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={pieStatiCom} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} label={(e) => `${e.name}: ${e.value}`}>
                    {pieStatiCom.map((entry, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Top venditori */}
        <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid="analytics-top-venditori">
          <div className="flex items-center gap-2 mb-4">
            <Trophy size={16} className="text-[#F59E0B]" />
            <h3 className="text-sm font-bold uppercase tracking-wide text-zinc-700">Classifica venditori (top 10)</h3>
          </div>
          {topVend.length === 0 ? (
            <div className="text-center text-zinc-500 py-8 text-sm">Nessun venditore con preventivi accettati nel periodo</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-zinc-500 border-b border-zinc-200">
                <tr>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Venditore</th>
                  <th className="px-3 py-2 text-right">Preventivi accettati</th>
                  <th className="px-3 py-2 text-right">Totale venduto</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {topVend.map((v, i) => (
                  <tr key={v.venditore_id} data-testid={`analytics-vend-row-${i}`}>
                    <td className="px-3 py-2 font-bold">{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}</td>
                    <td className="px-3 py-2">{v.nome}</td>
                    <td className="px-3 py-2 text-right">{v.count}</td>
                    <td className="px-3 py-2 text-right font-semibold text-emerald-700">{fmtEur(v.venduto)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Migrazione produzione */}
        <MigrazioneNegozioBox />
      </Page>
    </div>
  );
}

function MigrazioneNegozioBox() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [negozi, setNegozi] = useState([]);
  const [selected, setSelected] = useState("");
  useEffect(() => {
    api.get("/negozi").then((r) => {
      setNegozi(r.data || []);
      if ((r.data || []).length > 0) setSelected(r.data[0].id);
    }).catch(() => {});
  }, []);

  const run = async () => {
    if (!window.confirm("Assegnare il negozio selezionato a tutti i preventivi/commesse senza negozio? (operazione idempotente e sicura)")) return;
    setRunning(true);
    try {
      const r = await api.post("/admin/migrate-negozio-default", { negozio_id: selected });
      setResult(r.data);
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || e.message });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-5" data-testid="analytics-migrazione">
      <h3 className="text-sm font-bold uppercase tracking-wide text-amber-800 mb-2">🚀 Migrazione produzione — negozio_id</h3>
      <p className="text-xs text-amber-900 mb-3">
        Assegna un negozio di default a tutti i preventivi/commesse/lead che non hanno ancora un <code>negozio_id</code>.
        Utile per popolare i dati storici prima di attivare i filtri multi-store in produzione. È idempotente: non tocca chi ha già un negozio.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <select
          className="text-sm border border-amber-300 rounded-lg px-3 py-2 bg-white"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          data-testid="analytics-migrazione-select"
        >
          {negozi.map((n) => <option key={n.id} value={n.id}>{n.name || n.nome || n.id}</option>)}
        </select>
        <button
          onClick={run}
          disabled={!selected || running}
          className="text-sm bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 font-semibold"
          data-testid="analytics-migrazione-run"
        >
          {running ? "In corso…" : "Esegui migrazione"}
        </button>
        {result && (
          <div className="text-xs" data-testid="analytics-migrazione-result">
            {result.error
              ? <span className="text-rose-600 font-semibold">Errore: {result.error}</span>
              : <span className="text-emerald-700 font-semibold">✓ Migrati: {result.preventivi_migrati} preventivi · {result.commesse_migrate} commesse · {result.leads_migrati} lead</span>}
          </div>
        )}
      </div>
    </div>
  );
}
